// ExportVideosTable.jsx - the user's exported videos with live progress.
// Used by the editor's "My Videos" popup and the dashboard's "My Videos" tab.
import React, { useContext } from "react";
import axios from "axios";
import fileDownload from "js-file-download";
import Swal from "sweetalert";
import Swal2 from "sweetalert2";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";
import AuthContext from "../context/userAuth.context";
import { ACTIVE_STATES, describeExport, useMyExports, videoUrl } from "./exportApi";

function displayName(item) {
  const name = item.fileName || "Untitled";
  return name.replace(/\.(mp4|webm)$/i, "");
}

function ProgressCell({ item }) {
  const active = ACTIVE_STATES.includes(item.state);
  if (item.state === "Failed") {
    return (
      <Typography variant="body2" color="error">
        {item.error || "The export failed. Please try again."}
      </Typography>
    );
  }
  if (!active) return null;

  const pct = Math.max(0, Math.min(100, Math.round(item.progress || 0)));
  const indeterminate = item.state === "Queued" || item.state === "Encoding";
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant={indeterminate ? "indeterminate" : "determinate"} value={pct} />
      </Box>
      {!indeterminate && (
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">{`${pct}%`}</Typography>
        </Box>
      )}
    </Box>
  );
}

export default function ExportVideosTable({ apiBaseURL, enabled = true, setDeafultLoader }) {
  const { Access, getLink } = useContext(AuthContext);
  const { items, loading, error, cancel, remove } = useMyExports(apiBaseURL, enabled);
  const shareLocked = Access === "FE";

  const loader = (on) => setDeafultLoader && setDeafultLoader({ played: on, stat: on });

  const handleDownload = (item) => {
    const link = videoUrl(item);
    if (!link) return;
    axios
      .get(link, { responseType: "blob" })
      .then((res) => fileDownload(res.data, item.fileName || "video.mp4"))
      .catch(() => window.open(link, "_blank", "noopener"));
  };

  const confirmCancel = (item) => {
    Swal({
      title: "Stop this export?",
      text: "The video will not be created. You can export the project again later.",
      icon: "warning",
      buttons: ["Keep exporting", "Stop export"],
      dangerMode: true,
    }).then((ok) => {
      if (ok) cancel(item.id).catch(() => Swal("Failed", "Could not stop the export. Please try again.", "error"));
    });
  };

  const confirmDelete = (item) => {
    const running = ACTIVE_STATES.includes(item.state);
    Swal({
      title: "Are you sure?",
      text: running
        ? "This will stop the export and delete it."
        : "This will delete the video. Download it first if you want to keep it.",
      icon: "warning",
      buttons: [true, running ? "Stop and delete" : "Delete video"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      loader(true);
      remove(item.id)
        .catch(() => Swal("Failed", "Could not delete the video. Please try again.", "error"))
        .finally(() => loader(false));
    });
  };

  const showUpgrade = () => {
    if (!shareLocked) return;
    Swal2.fire({
      title: "Upgrade Required",
      html: `You have not purchased this upgrade yet. Please <a href=${getLink()} target='_blank'>upgrade</a> to unlock all features.`,
      icon: "info",
      backdrop: null,
    });
  };

  const shareStyle = { margin: "2px", opacity: shareLocked ? 0.5 : 1 };

  return (
    <>
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Table aria-label="exported videos">
        <TableHead>
          <TableRow>
            <TableCell>Project Name</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Action</TableCell>
            <TableCell onClick={showUpgrade} style={{ cursor: shareLocked ? "pointer" : "default" }}>
              Share
              {shareLocked && (
                <img
                  className="iconUS"
                  src="../AppIcons/IconsAll/PremiumIcon.svg"
                  alt=""
                  style={{ width: "20px", height: "20px", marginLeft: "8px" }}
                />
              )}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading && items.length === 0 && !error && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No videos yet. Use Export in the editor to create one.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {items.map((item) => {
            const done = item.state === "Completed" && !!item.videoPath;
            const active = ACTIVE_STATES.includes(item.state);
            const link = videoUrl(item);
            return (
              <TableRow key={item.id}>
                <TableCell>
                  {displayName(item)}
                  {item.format === "webm" && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      WebM
                    </Typography>
                  )}
                </TableCell>
                <TableCell style={{ width: "220px" }}>
                  {done ? (
                    <Button onClick={() => handleDownload(item)} variant="outlined" size="small" startIcon={<DownloadIcon />}>
                      Download
                    </Button>
                  ) : (
                    <ProgressCell item={item} />
                  )}
                </TableCell>
                <TableCell>{describeExport(item)}</TableCell>
                <TableCell>
                  <Stack direction="row">
                    {active && !item.cancelRequested && (
                      <Tooltip title="Stop export">
                        <IconButton onClick={() => confirmCancel(item)} aria-label="stop export">
                          <StopCircleOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton onClick={() => confirmDelete(item)} aria-label="delete">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    {done && (
                      <Tooltip title="Copy link">
                        <IconButton onClick={() => navigator.clipboard.writeText(link)} aria-label="copy link">
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {done && (
                    <Stack direction="row">
                      <LinkedinShareButton url={link} style={shareStyle} disabled={shareLocked}>
                        <LinkedinIcon size={24} round />
                      </LinkedinShareButton>
                      <FacebookShareButton url={link} quote={"app.toons.ai"} style={shareStyle} disabled={shareLocked}>
                        <FacebookIcon size={24} round />
                      </FacebookShareButton>
                      <TwitterShareButton url={link} style={shareStyle} disabled={shareLocked}>
                        <TwitterIcon size={24} round />
                      </TwitterShareButton>
                      <WhatsappShareButton url={link} separator=":: " style={shareStyle} disabled={shareLocked}>
                        <WhatsappIcon size={24} round />
                      </WhatsappShareButton>
                      <EmailShareButton url={link} style={shareStyle} disabled={shareLocked}>
                        <EmailIcon size={24} round />
                      </EmailShareButton>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
}
